/*
 * @Copyright 2024. Institute for Future Intelligence, Inc.
 */

import { Vector3 } from 'three';
import { TWO_PI } from '../constants.ts';
import { EV_CONVERTER, GF_CONVERSION_CONSTANT } from './physicalConstants.ts';
import Element from '../lib/chem/Element';
import { Restraint } from './Restraint.ts';
import { getUffLj } from './uffParameters.ts';
import type { BeadType } from '../types.ts';

export class Atom {
  // essential variables that should be persisted
  elementSymbol: string;
  position: Vector3;
  velocity: Vector3;

  // the rest of the variables that can be created later (no need to save)
  index: number;
  mass: number = 10;
  sigma: number = 1; // van der Waals radius
  epsilon: number = 0.05; // van der Waals energy
  charge: number = 0;
  // The charge was set BY THE USER (Charge field, agent setLabAtomProperty), not
  // computed (Gasteiger / formal). Such a charge — zero included — is kept across
  // engine rebuilds and persisted on Save; a computed one is re-derived on load.
  chargeSet?: boolean;

  restraint?: Restraint;
  fixed?: boolean;
  trajectory?: boolean;
  damp?: number;
  // Marks an atom as inert for pair-interaction kernels — it stays in the
  // atoms[] array so indices remain stable, but the kernel skips it. Two uses:
  // Crystal-MD, an atom cut from the chamber (also `fixed`, so predict/correct
  // early-returns; bonded loops produce no force on it; the crystal viewer
  // doesn't render it; cleared on supercell reload, preserved across reset);
  // and docking, a crystal water excluded from the receptor (DockingScore
  // skips it — see buildProteinScoreAtoms).
  removed?: boolean;

  // Render-only periodic-image copy used by the crystal viewer to draw
  // bonds across the supercell boundary as short stubs. Never seen by the
  // MD engine (ghosts are appended to the visual atom list AFTER the
  // scaffold is built, so configureCrystalDynamics never receives them).
  // Renderer uses this flag to skip pick handlers and cluster-highlight
  // logic.
  isGhost?: boolean;

  // For a ghost atom only: the index (into the real-atom list) of the
  // atom this ghost is a periodic image of. The renderer reads the live
  // source atom's kinetic energy through this so a ghost inherits the
  // same white→red KE color as its source — ghost positions are frozen
  // at build time, but its color tracks the moving source each frame.
  ghostSource?: number;

  // A ghost generated purely to COMPLETE the lattice at the supercell
  // boundary (a corner/face/edge image of an atomic crystal), not because
  // it terminates a bond — see computeCosmeticBoundaryAtoms. Also carries
  // isGhost, so the renderer/MD treat it like any other image; this extra
  // flag just marks its cosmetic provenance for any caller that wants to
  // exclude the decorative atoms (e.g. structure export).
  isCosmetic?: boolean;

  // For an image atom (isGhost): the CONSTANT lattice translation from its
  // source atom — ghostPosition = source.position + imageOffset. A periodic
  // image rigidly follows its source, so the crystal viewer re-derives the
  // image position from the live source each frame through this offset,
  // keeping the boundary atoms locked to the lattice as it vibrates under
  // MD instead of leaving them frozen at their build-time positions.
  imageOffset?: Vector3;

  displacement?: Vector3;
  acceleration?: Vector3;
  force?: Vector3;
  initialPosition?: Vector3;
  initialVelocity?: Vector3;

  moleculeName?: string;

  // Crystal-MD: the id of the guest molecule this atom belongs to — a
  // guest inserted into the chamber supercell (a clathrate guest, dopant,
  // solute), not part of the periodic basis. Set ⇒ this is a guest atom:
  // the crystal force fields skip it (guests carry their own σ/ε/charge
  // from the guest library) and the transform gizmo writes drags of it
  // back to ProjectState.chamberGuests by this id.
  guestId?: string;

  // Molecular lab, coarse-grained resolutions: the bead type this particle is
  // (a CH₂ united atom, a polymer bead, a rod). Set by setBead, which also
  // overrides the mass / σ / ε the constructor read from the element and UFF
  // tables for the placeholder element. Undefined ⇒ an ordinary atom. Not
  // persisted: a saved molecule's bead model re-applies it on load.
  bead?: BeadType;

  // Gay-Berne rods only (gayBerne.ts): the rod's long axis (a unit vector),
  // its angular velocity (rad/fs, kept ⊥ to the axis) and the axis it resets
  // to. Set by setBead for a bead type with an aspect ratio above one.
  orientation?: Vector3;
  angularVelocity?: Vector3;
  initialOrientation?: Vector3;

  constructor(index: number, elementSymbol: string, position: Vector3, init?: boolean) {
    this.index = index;
    this.elementSymbol = elementSymbol;
    this.position = position;
    this.velocity = new Vector3();
    this.force = new Vector3();
    if (init) {
      this.displacement = new Vector3();
      this.acceleration = new Vector3();
      this.initialPosition = new Vector3();
      this.initialVelocity = new Vector3();
    }
    const elem = Element.getByName(elementSymbol);
    this.mass = elem.weight;
    const uff = getUffLj(elementSymbol);
    if (uff) {
      this.sigma = uff.sigma;
      this.epsilon = uff.epsilon;
    } else {
      this.sigma = elem.radius;
    }
  }

  /** Make this atom a bead of the given type: its mass, σ, ε and (unless set by the user) charge. */
  setBead(type: BeadType): void {
    this.bead = type;
    this.mass = type.mass;
    this.sigma = type.sigma;
    this.epsilon = type.epsilon;
    if (!this.chargeSet) this.charge = type.charge ?? 0;
    if ((type.aspect ?? 1) > 1) {
      if (!this.orientation) this.orientation = new Vector3(0, 0, 1);
      if (!this.angularVelocity) this.angularVelocity = new Vector3();
      if (!this.initialOrientation) this.initialOrientation = this.orientation.clone();
    }
  }

  // for atom serialized from Firestore, set full to be true
  static clone(atom: Atom, full?: boolean): Atom {
    const newAtom = new Atom(
      atom.index,
      atom.elementSymbol,
      new Vector3(atom.position.x, atom.position.y, atom.position.z),
    );
    newAtom.velocity.copy(atom.velocity);
    newAtom.force = new Vector3();
    if (atom.displacement) {
      newAtom.displacement = new Vector3();
    }
    if (atom.acceleration) {
      newAtom.acceleration = new Vector3();
    }
    // The Reset target (initialPosition / initialVelocity) belongs to the SAME
    // atom coming back — an undo snapshot, a Firestore round trip — so only a
    // full clone carries it; a default clone is a NEW molecule (Copy / Paste),
    // which must start where it is placed, not snap back to the original's
    // start on Reset. The value is copied: a bare new Vector3() left every
    // cloned target at the origin.
    if (full && atom.initialPosition) {
      newAtom.initialPosition = new Vector3(atom.initialPosition.x, atom.initialPosition.y, atom.initialPosition.z);
    }
    if (full && atom.initialVelocity) {
      newAtom.initialVelocity = new Vector3(atom.initialVelocity.x, atom.initialVelocity.y, atom.initialVelocity.z);
    }
    if (atom.bead) {
      newAtom.setBead(atom.bead);
      if (atom.orientation && newAtom.orientation) newAtom.orientation.copy(atom.orientation as Vector3);
      if (atom.angularVelocity && newAtom.angularVelocity)
        newAtom.angularVelocity.copy(atom.angularVelocity as Vector3);
      if (atom.initialOrientation && newAtom.initialOrientation)
        newAtom.initialOrientation.copy(atom.initialOrientation as Vector3);
    } else if (atom.orientation) {
      // Back from Firestore the atom's bead is stripped but its axis is saved:
      // Molecule.clone's setBead keeps an axis already there.
      newAtom.orientation = new Vector3().copy(atom.orientation as Vector3);
      if (atom.angularVelocity) newAtom.angularVelocity = new Vector3().copy(atom.angularVelocity as Vector3);
      if (atom.initialOrientation) newAtom.initialOrientation = new Vector3().copy(atom.initialOrientation as Vector3);
    }
    newAtom.fixed = atom.fixed;
    newAtom.charge = atom.charge;
    if (atom.chargeSet) newAtom.chargeSet = true;
    if (full) {
      if (atom.restraint) {
        // cannot use Vector3.clone() as the object from Firestore is not actually a Vector3
        const p = new Vector3(atom.restraint.position.x, atom.restraint.position.y, atom.restraint.position.z);
        newAtom.restraint = new Restraint(atom.restraint.strength, p);
      }
      newAtom.damp = atom.damp;
      newAtom.trajectory = atom.trajectory;
    }
    return newAtom;
  }

  reset() {
    if (this.initialPosition) {
      this.position.copy(this.initialPosition);
    }
    if (this.initialOrientation && this.orientation) {
      this.orientation.copy(this.initialOrientation);
      this.angularVelocity?.set(0, 0, 0);
    }
    if (this.initialVelocity) {
      this.velocity.copy(this.initialVelocity);
    }
    // A pinned atom carries no motion. Its saved initialVelocity may predate the
    // pin (the lab was loaded moving, then the atom was fixed), and the integrator
    // skips fixed atoms, so that velocity would otherwise sit on it until it was
    // released — and then launch it at the old speed.
    if (this.fixed) {
      this.velocity.set(0, 0, 0);
      this.acceleration?.set(0, 0, 0);
    }
  }

  distanceTo(target: Atom): number {
    return this.position.distanceTo(target.position);
  }

  distanceToSquared(target: Atom): number {
    return this.position.distanceToSquared(target.position);
  }

  /* given the speed scalar, assign a velocity vector in a random direction */
  setRandomVelocity(speed: number) {
    if (!this.velocity) return;
    const theta = TWO_PI * Math.random();
    const phi = TWO_PI * Math.random();
    this.velocity.x = speed * Math.cos(phi) * Math.sin(theta);
    this.velocity.y = speed * Math.sin(phi) * Math.sin(theta);
    this.velocity.z = speed * Math.cos(theta);
  }

  getKineticEnergy() {
    if (!this.velocity || !this.mass) return 0;
    return 0.5 * this.mass * this.velocity.lengthSq() * EV_CONVERTER;
  }

  // calculate force and return potential energy: v(r)=k*(ri-ri_0)^2/2
  computeRestraint(): number {
    if (this.restraint && this.restraint.strength > 0) {
      const k = (this.restraint.strength * GF_CONVERSION_CONSTANT) / this.mass;
      const dx = this.position.x - this.restraint.position.x;
      const dy = this.position.y - this.restraint.position.y;
      const dz = this.position.z - this.restraint.position.z;
      if (this.force) {
        this.force.x -= k * dx;
        this.force.y -= k * dy;
        this.force.z -= k * dz;
      }
      return 0.5 * this.restraint.strength * (dx * dx + dy * dy + dz * dz);
    }
    return 0;
  }

  // Viscous drag F = -damp·v (damp in eV·fs/Å², the unit the context menu and the
  // agent's setAtomAttribute advertise). `force` holds the acceleration (GF·F/m,
  // as computeRestraint above), so the coefficient is divided by the mass here.
  // It wasn't: the drag was -damp·m·v, so a 127 amu iodine felt 127× the drag
  // force of a hydrogen at the same coefficient and speed (equivalently, every
  // atom's velocity decayed at the same rate regardless of mass) — the opposite
  // of what a solvent-like drag does and inconsistent with the restraint beside it.
  computeDamp() {
    if (this.damp && this.mass > 0) {
      const d = (GF_CONVERSION_CONSTANT * this.damp) / this.mass;
      if (this.force) {
        this.force.x -= d * this.velocity.x;
        this.force.y -= d * this.velocity.y;
        this.force.z -= d * this.velocity.z;
      }
    }
  }

  /* predict new position using 2nd order Taylor expansion: dt2 = dt*dt/2 */
  predict(dt: number, dt2: number) {
    if (this.fixed) return;
    if (!this.displacement || !this.velocity || !this.acceleration) return;
    this.displacement.x = this.velocity.x * dt + this.acceleration.x * dt2;
    this.displacement.y = this.velocity.y * dt + this.acceleration.y * dt2;
    this.displacement.z = this.velocity.z * dt + this.acceleration.z * dt2;
    this.position.x += this.displacement.x;
    this.position.y += this.displacement.y;
    this.position.z += this.displacement.z;
    this.velocity.x += this.acceleration.x * dt;
    this.velocity.y += this.acceleration.y * dt;
    this.velocity.z += this.acceleration.z * dt;
  }

  /*
   * correct the predicted position. Note that the force was used in the force calculation routine
   * to store the new acceleration data. The acceleration was used to hold the old acceleration
   * before calculating the force. After that, new acceleration will be assigned. Be aware that the
   * acceleration and force are correct ONLY after this correction method has been called.
   *
   * h = half of the time increment
   */
  correct(h: number) {
    if (this.fixed) return;
    if (!this.force || !this.velocity || !this.acceleration || !this.mass) return;
    this.velocity.x += h * (this.force.x - this.acceleration.x);
    this.velocity.y += h * (this.force.y - this.acceleration.y);
    this.velocity.z += h * (this.force.z - this.acceleration.z);
    this.acceleration.copy(this.force);
    this.force.multiplyScalar(this.mass);
  }
}
